import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import BroadcastMessageBody from '@/components/BroadcastMessageBody';
import { Badge, CountBadge, IconButton, Popover, Skeleton, cx, focusRing } from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { playClickSound } from '@/utils/soundUtils';

const POLL_MS = 2 * 60 * 1000;

type BroadcastRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  acknowledged: boolean;
};

function formatBroadcastTime(
  iso: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (iso == null || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return t('notifications.todayAt', {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    });
  }
  if (diffHours < 48) {
    return t('notifications.yesterdayAt', {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    });
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface KioskNotificationBellProps {
  compact?: boolean;
}

/**
 * Header bell for admin broadcasts: a Popover anchored to the bell (rendered on
 * <body>, because the header's backdrop blur would trap a fixed child). Loads on
 * open and every two minutes; Escape and outside click close it.
 */
export default function KioskNotificationBell({ compact: _compact = false }: KioskNotificationBellProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<BroadcastRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getMyAdminBroadcasts();
      if (res.success && res.data?.broadcasts) {
        setItems(res.data.broadcasts);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const onAuth = () => void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  const unreadCount = items.filter((b) => !b.acknowledged).length;

  const handleRead = useCallback(async (id: string, isRead: boolean) => {
    playClickSound();
    if (isRead) return;
    await apiService.acknowledgeAdminBroadcast(id);
    await load();
  }, [load]);

  // No bulk endpoint for players: ack each unread one at once (the server ignores repeats), show
  // them read straight away, then reload so anything that failed comes back as unread.
  const markingAllRef = useRef(false);
  const handleMarkAllRead = useCallback(
    async (panel: HTMLElement | null) => {
      if (markingAllRef.current) return;
      const unreadIds = items.filter((b) => !b.acknowledged).map((b) => b.id);
      if (unreadIds.length === 0) return;
      playClickSound();
      markingAllRef.current = true;
      setItems((prev) => prev.map((b) => (b.acknowledged ? b : { ...b, acknowledged: true })));
      // The button is about to disappear: keep focus in the panel so Escape still closes it.
      panel?.focus({ preventScroll: true });
      try {
        await Promise.allSettled(unreadIds.map((id) => apiService.acknowledgeAdminBroadcast(id)));
        await load();
      } finally {
        markingAllRef.current = false;
      }
    },
    [items, load]
  );

  return (
    <div className="relative shrink-0">
      <Popover
        aria-label={t('notifications.listAriaLabel')}
        align="end"
        width={280}
        className="cosmos-pop-in font-ui text-small"
        onOpenChange={(open) => {
          if (open) void load();
        }}
        trigger={(props) => (
          <IconButton {...props} variant="neutral" size="md" label={t('notifications.ariaLabel')} icon={<Bell strokeWidth={2.2} />} />
        )}
      >
              <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-edge-subtle px-3 py-2.5">
                <p className="m-0 font-semibold text-primary">{t('notifications.title')}</p>
                {unreadCount > 0 ? (
                  <>
                    <Badge tone="primary" size="sm" dot={false}>
                      {t('notifications.newCount', { count: unreadCount })}
                    </Badge>
                    <button
                      type="button"
                      onClick={(e) => void handleMarkAllRead(e.currentTarget.closest<HTMLElement>('[role="dialog"]'))}
                      className={cx(
                        'ml-auto shrink-0 rounded-control font-medium text-link underline underline-offset-2 hover:text-primary',
                        focusRing
                      )}
                    >
                      {t('notifications.markAllRead')}
                    </button>
                  </>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 cosmos-scroll-y">
                {loading && items.length === 0 ? (
                  <div className="space-y-2 px-3 py-2.5">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div key={idx} className="rounded-control bg-inset px-2.5 py-2">
                        <Skeleton className="h-2.5 w-20" />
                        <Skeleton className="mt-1.5 h-2 w-full" />
                        <Skeleton className="mt-1 h-2 w-2/3" />
                      </div>
                    ))}
                  </div>
                ) : items.length === 0 ? (
                  <p className="m-0 px-3 py-6 text-center text-tertiary">{t('notifications.none')}</p>
                ) : (
                  <ul className="m-0 list-none p-0">
                    {items.map((b) => {
                      const timeLabel = formatBroadcastTime(b.createdAt, t);
                      const isRead = Boolean(b.acknowledged);
                      return (
                        <li key={b.id}>
                          <div
                            role="button"
                            tabIndex={0}
                            className={cx(
                              'flex w-full cursor-pointer items-start gap-2 border-b border-edge-subtle px-3 py-2.5 text-left transition-colors last:border-b-0',
                              'hover:bg-hover focus-visible:bg-selected focus-visible:outline-none'
                            )}
                            onClick={() => void handleRead(b.id, isRead)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void handleRead(b.id, isRead);
                              }
                            }}
                          >
                            <span
                              className={cx(
                                'mt-1.5 size-1.5 shrink-0 rounded-full',
                                isRead ? 'bg-transparent' : 'bg-action-primary'
                              )}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1">
                              <span
                                className={cx(
                                  'mb-0.5 block font-semibold leading-snug',
                                  isRead ? 'text-secondary' : 'text-primary'
                                )}
                              >
                                {b.title || 'Notification'}
                              </span>
                              <span
                                className={cx(
                                  'mb-1 block whitespace-pre-wrap leading-snug',
                                  isRead ? 'text-tertiary' : 'text-secondary'
                                )}
                              >
                                <BroadcastMessageBody
                                  text={b.body}
                                  linkClassName="break-all text-link underline underline-offset-2"
                                  onLinkClick={(e) => e.stopPropagation()}
                                />
                              </span>
                              {timeLabel ? <span className="block text-tertiary">{timeLabel}</span> : null}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
      </Popover>
      {unreadCount > 0 ? (
        <span className="pointer-events-none absolute -right-1 -top-1" aria-hidden>
          <CountBadge count={unreadCount} />
        </span>
      ) : null}
    </div>
  );
}
